import { useEffect, useRef, useState } from 'react'
import { mutate } from 'swr'
import type { NylasMessage, NylasThread } from '@equaliq/api-model'
import { API_BASE_URL } from 'src/constants/api.constants'

/**
 * Server-Sent Events subscription for the user's Nylas inbox.
 *
 * Replaces SWR-poll-on-error as the post-snapshot delta source.
 * The snapshot still loads via the existing useNylasMessages /
 * useNylasThreads hooks; this hook handles every update after that
 * by listening to the SSE Lambda's pg LISTEN channel.
 *
 * Auth: SSE doesn't allow custom request headers, so the Clerk session
 * token is passed in the query string. Clerk tokens are short-lived
 * (default 60s) and the EventSource auto-reconnect rotates the token
 * each time. The SSE Lambda verifies the token via Clerk's public JWKS.
 *
 * Wire format on the server side: each pg notification arrives as
 * `event: message\ndata: <json>\n\n` where the data envelope is
 * `{ kind: 'message', message: NylasMessage }` or
 * `{ kind: 'invalidate', messageId: string }` for oversize payloads.
 *
 * Cache strategy: on each new message we mutate the SWR caches for
 * `['nylas-messages', connectionId]` and `['nylas-threads', connectionId, false]`
 * with `false` as the revalidate flag so SWR doesn't immediately revalidate —
 * the SSE delta IS the truth. A revalidate would race the snapshot.
 *
 * The threads key carries a third element `false` = the DEAL-ONLY default view
 * (R1-a keyed useNylasThreads on includeUnlinked). The SSE optimistic merge only
 * concerns that default lens — the "Needs review / All mail" escape-hatch view
 * is an on-demand lens, not a live-stream surface — so mutating the `false` slot
 * keeps Tier-C liveness on the default path intact without touching the
 * escape-hatch slot.
 */
export function useNylasInboxStream(
  connectionId: string | null,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  const [status, setStatus] = useState<'idle' | 'connecting' | 'open' | 'error' | 'closed'>('idle')
  const [lastEventId, setLastEventId] = useState<string | null>(null)
  // Hold the EventSource in a ref so React StrictMode's double-mount
  // doesn't end up with two parallel connections.
  const esRef = useRef<EventSource | null>(null)

  useEffect(() => {
    if (!enabled || !connectionId) {
      setStatus('idle')
      return
    }

    // API_BASE_URL resolves from environments.json[tag].apiEndpoint.
    // Strip trailing slashes — see useDealProgressStream (#254): leading
    // `//` parses as protocol-relative and EventSource never opens.
    const apiBase = API_BASE_URL.replace(/\/+$/, '')
    const SSE_URL = import.meta.env.VITE_NYLAS_INBOX_STREAM_URL
      || `${apiBase}/sse/inbox`

    let cancelled = false
    let es: EventSource | null = null

    async function connect() {
      // Mint a fresh Clerk session token. The SDK exposes this via the
      // window.Clerk global once ClerkProvider mounts; same pattern the
      // axios interceptor uses for Bearer auth on REST calls.
      const clerk = (typeof window !== 'undefined' ? (window as any).Clerk : null)
      const token: string | undefined = await clerk?.session?.getToken?.()
      if (!token) {
        console.warn('[useNylasInboxStream] no Clerk token available — skipping connect')
        setStatus('error')
        return
      }
      if (cancelled) return

      setStatus('connecting')
      const url = `${SSE_URL}?connectionId=${encodeURIComponent(connectionId!)}&token=${encodeURIComponent(token)}`
      es = new EventSource(url)
      esRef.current = es

      es.addEventListener('open', () => {
        if (cancelled) return
        setStatus('open')
        console.log('[useNylasInboxStream] connected to SSE')
      })

      // Server-emitted handshake — confirms the channel is live.
      es.addEventListener('open' as any, () => {})

      es.addEventListener('message', (ev: MessageEvent<string>) => {
        if (cancelled) return
        if (ev.lastEventId) setLastEventId(ev.lastEventId)
        let payload: any
        try {
          payload = JSON.parse(ev.data)
        } catch (err) {
          console.warn('[useNylasInboxStream] non-JSON SSE payload:', ev.data)
          return
        }
        if (payload?.kind === 'message' && payload.message) {
          mergeIntoCaches(connectionId!, payload.message as NylasMessage)
        } else if (payload?.kind === 'invalidate') {
          // Server sent an envelope-too-large notice — refetch the
          // snapshot for this connection so the missed message lands.
          // The threads key carries `false` = the deal-only default view the
          // hook reads (R1-a keyed useNylasThreads on includeUnlinked).
          mutate(['nylas-messages', connectionId!])
          mutate(['nylas-threads', connectionId!, false])
        }
      })

      es.addEventListener('error', (ev) => {
        if (cancelled) return
        console.warn('[useNylasInboxStream] SSE error', ev)
        setStatus('error')
        // EventSource handles its own reconnect (every 5s per server's
        // retry: directive). On true close (4xx auth failure), it stops.
      })
    }

    void connect()

    return () => {
      cancelled = true
      if (es) {
        es.close()
        es = null
      }
      esRef.current = null
      setStatus('closed')
    }
  }, [enabled, connectionId])

  return { status, lastEventId }
}

/**
 * Merge an incoming message into the SWR caches without triggering a
 * server revalidation. The message is prepended to the messages list
 * and the corresponding thread (if any) is bumped to the top of the
 * threads list. New threads are inserted at the top so they appear
 * immediately in the inbox UI.
 *
 * The `false` third arg to mutate() = no revalidate. SSE IS the truth.
 */
function mergeIntoCaches(connectionId: string, msg: NylasMessage): void {
  // Messages cache: prepend, dedupe by id.
  mutate(
    ['nylas-messages', connectionId],
    (prev: NylasMessage[] | undefined) => {
      const existing = prev ?? []
      if (existing.some((m) => m.id === msg.id)) return existing
      return [msg, ...existing]
    },
    { revalidate: false },
  )

  // Threads cache: bump (or insert) the matching thread to the top. Keyed on
  // the deal-only default view (`false`) the hook reads — the SSE optimistic
  // merge is a default-lens concern, not an escape-hatch one.
  mutate(
    ['nylas-threads', connectionId, false],
    (prev: NylasThread[] | undefined) => {
      const existing = prev ?? []
      const idx = existing.findIndex(
        (t) => t.externalThreadId === msg.threadId || t.threadMetadataId === msg.threadId,
      )
      const lastMessageAt = new Date((msg.date ?? 0) * 1000).toISOString()
      if (idx >= 0) {
        const bumped: NylasThread = {
          ...existing[idx],
          lastMessageAt,
          messageCount: (existing[idx].messageCount ?? 0) + 1,
          summary: existing[idx].summary || msg.subject,
        }
        return [bumped, ...existing.slice(0, idx), ...existing.slice(idx + 1)]
      }
      // Brand new thread — synthesize a minimal NylasThread row from the
      // message so the UI has something to render until the next snapshot.
      const newThread: NylasThread = {
        threadMetadataId: msg.threadId ?? msg.id,
        externalThreadId: msg.threadId ?? msg.id,
        provider: 'google',
        lastMessageAt,
        messageCount: 1,
        summary: msg.subject,
        analyzed: false,
        createdAt: lastMessageAt,
        updatedAt: lastMessageAt,
      }
      return [newThread, ...existing]
    },
    { revalidate: false },
  )
}
